<template>
  <div>
    <!-- Overlay for mobile view when sidebar is open -->
    <div v-if="isSidebarOpen" @click="toggleSidebar" class="sidebar-overlay"></div>

    <!-- Sidebar -->
    <div class="app-sidebar" :class="{ 'is-open': isSidebarOpen }">
      <router-link to="/" class="logo-container flex justify-center">
        <img :src="logoSrc" alt="Inaubi Logo" class="h-10 w-auto" />
      </router-link>
      <nav class="nav-menu">
        <ul>
          <li>
            <router-link to="/jarvin" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-comments"></i>
              <span>Jarvin</span>
            </router-link>
          </li>
          <li>
            <router-link to="/mail-security" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-shield"></i>
              <span>Sécurité mail</span>
            </router-link>
          </li>
          <li>
            <a href="https://www.inaubi.fr" target="_blank" rel="noopener noreferrer" class="nav-item" @click="onLinkClick">
              <i class="pi pi-globe"></i>
              <span>Site web Inaubi</span>
              <i class="pi pi-external-link ml-auto external-link-icon"></i>
            </a>
          </li>
          <li>
            <div class="admin-section-title">Google Workspace</div>
            <router-link to="/google-workspace/status" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-chart-line"></i>
              <span>Statut</span>
            </router-link>
          </li>
          <li>
            <router-link to="/google-workspace/admin-tool" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-cog"></i>
              <span>Admin tool</span>
            </router-link>
          </li>
          <!-- <li>
            <router-link to="/google-workspace/nouveautes" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-megaphone"></i>
              <span>Nouveautés</span>
            </router-link>
          </li> -->
          <li>
            <router-link to="/google-workspace/release-calendar" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-calendar"></i>
              <span>Release Calendar</span>
            </router-link>
          </li>
          <li>
            <a href="https://github.com/GAM-team/GAM" target="_blank" rel="noopener noreferrer" class="nav-item" @click="onLinkClick">
              <i class="pi pi-wrench"></i>
              <span>GAM</span>
              <i class="pi pi-external-link ml-auto external-link-icon"></i>
            </a>
          </li>
          <li>
            <div class="admin-section-title">LumApps</div>
            <a href="https://status.lumapps.com/posts/dashboard" target="_blank" rel="noopener noreferrer" class="nav-item" @click="onLinkClick">
              <i class="pi pi-chart-line"></i>
              <span>Statut</span>
              <i class="pi pi-external-link ml-auto external-link-icon"></i>
            </a>
          </li>
          <li>
            <a href="https://docs.lumapps.com" target="_blank" rel="noopener noreferrer" class="nav-item" @click="onLinkClick">
              <i class="pi pi-book"></i>
              <span>Documentation</span>
              <i class="pi pi-external-link ml-auto external-link-icon"></i>
            </a>
          </li>
          <!-- Admin Section -->
          <li v-if="uiStore.isAdminSectionVisible">
             <div class="admin-section-title">Admin</div>
            <router-link to="/admin/organizations" class="nav-item" active-class="active" @click="onLinkClick">
              <i class="pi pi-building"></i>
              <span>Organisations</span>
            </router-link>
          </li>
        </ul>
      </nav>
      <div class="bottom-container">
        <AppPalette />
        <button v-if="authStore.isAdmin" @click="uiStore.toggleAdminSection" class="nav-item">
            <i class="pi pi-key"></i>
            <span>Accès Admin</span>
        </button>
        <button @click="authStore.logout" class="nav-item logout-button">
            <i class="pi pi-sign-out"></i>
            <span>Déconnexion</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useUIStore } from '../stores/ui';
import { useAuthStore } from '../stores/auth';
import { useThemeStore } from '../stores/theme';
import { storeToRefs } from 'pinia';
import AppPalette from './AppPalette.vue';

const uiStore = useUIStore();
const authStore = useAuthStore();
const themeStore = useThemeStore();

const { isDarkMode } = storeToRefs(themeStore);
const isSidebarOpen = computed(() => uiStore.isSidebarOpen);

const logoSrc = computed(() => {
  return isDarkMode.value ? '/inaubi_white.png' : '/inaubi_colour.png';
});

const toggleSidebar = () => {
  uiStore.toggleSidebar();
};

const onLinkClick = () => {
    if (window.innerWidth < 768) {
        uiStore.closeSidebar();
    }
}
</script>

<style scoped>
.app-sidebar {
  width: 250px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: var(--p-surface-card);
  color: var(--p-text-color);
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem;
  border-right: 1px solid var(--p-surface-border);
  transition: transform 0.3s ease-in-out, background-color 0.3s ease, color 0.3s ease;
  z-index: 100;
}

.logo-container {
  display: flex;
  align-items: center;
  margin-bottom: 2.5rem;
  padding: 0.5rem 1rem; /* Adjusted padding */
}

.logo-container img {
  max-height: 55px;
}

.nav-menu {
    flex-grow: 1;
}

.nav-menu ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.nav-menu li {
  margin-bottom: 0.5rem;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  text-decoration: none;
  color: var(--p-text-color-secondary);
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
}

.nav-item:hover {
  background-color: var(--p-surface-hover);
  color: var(--p-text-color);
}

.nav-item.active {
  background-color: var(--p-primary-500);
  color: var(--p-primary-contrast-color);
  font-weight: 600;
}



.external-link-icon {
  font-size: 0.65rem !important;
}

.nav-item i {
  margin-right: 1rem;
  font-size: 1.25rem;
}

.admin-section-title {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--p-text-color-secondary);
    text-transform: uppercase;
}

.bottom-container {
    margin-top: auto; /* Pushes content to the bottom */
}

.sidebar-overlay {
    display: none;
}

/* Responsive Styles */
@media (max-width: 767px) {
  .app-sidebar {
    transform: translateX(-100%);
  }
  .app-sidebar.is-open {
    transform: translateX(0);
  }
  .sidebar-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99;
    display: block;
  }
}
</style>